import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DifferenceIcon from "@mui/icons-material/Difference";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import SendIcon from "@mui/icons-material/Send";
import ThumbUpIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDownAltOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import { InputAdornment } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  tariffsApi, insuranceSchemesApi, currenciesApi,
  type TariffDto, type TariffImportResultDto, type TariffImportRowRequest,
  type TariffStatus, type UpsertTariffRequest, type ConvertedChargeRow,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { RowActions } from "@/components/RowActions";
import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/store/auth";
import { todayIso } from "@/shared/datetime";

const STATUS_TONE: Record<TariffStatus, "neutral" | "success" | "warning" | "info"> = {
  Draft: "neutral",
  // Out for approval reads as "someone has to do something"; approved reads as ready but not live.
  Submitted: "warning",
  Approved: "info",
  Active: "success",
  Superseded: "warning",
};

const today = todayIso;

/** A card being reviewed is frozen — nothing on it, or about it, can change (W16). */
const frozen = (t: TariffDto) => t.status === "Submitted" || t.status === "Approved";

/** "14 Aug 2026, 16:22" — an approval record is worth reading to the minute. */
const stamp = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";

/**
 * Rate cards (W11) — the header that service charge rows and price-list lines belong to.
 *
 * The screen is built around the one distinction that matters: a card in Draft prices nothing.
 * That is what lets a two-thousand-line payer card be built and checked before it touches a bill,
 * so the status column and the activate action are the primary things here, not the CRUD.
 */
export function TariffsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TariffStatus | "">("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [editing, setEditing] = useState<TariffDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importing, setImporting] = useState<TariffDto | null>(null);
  const [versioning, setVersioning] = useState<TariffDto | null>(null);
  const [reporting, setReporting] = useState<TariffDto | null>(null);
  const [rejecting, setRejecting] = useState<TariffDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("pricing.tariff.manage"));
  const canActivate = useAuth(s => s.hasPermission("pricing.tariff.activate"));
  const canApprove = useAuth(s => s.hasPermission("pricing.tariff.approve"));
  const userId = useAuth(s => s.user?.id);

  const query = useQuery({
    queryKey: ["tariffs", search, statusFilter, pagination.page, pagination.pageSize],
    queryFn: () => tariffsApi.list({
      search: search || undefined,
      status: statusFilter || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  // W16 — the queue, asked for separately so it is visible whatever the grid is filtered to. Only
  // cards somebody else put up count: the ones you submitted are not waiting on you.
  const queue = useQuery({
    queryKey: ["tariffs", "awaiting-approval"],
    queryFn: () => tariffsApi.list({ status: "Submitted", pageSize: 100 }),
    enabled: canApprove,
    staleTime: 60_000,
  });
  const awaitingMe = (queue.data?.items ?? []).filter(t => t.submittedBy !== userId).length;

  const statusMutation = useMutation({
    meta: { successMessage: "Rate card updated" },
    mutationFn: ({ id, status }: { id: string; status: TariffStatus }) =>
      tariffsApi.setStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tariffs"] }),
  });

  // W16 — the review path. One mutation for the three acts that carry no extra input; rejection
  // has its own dialog because it has to collect a reason.
  const reviewMutation = useMutation({
    meta: { successMessage: "Rate card updated" },
    mutationFn: ({ id, act }: { id: string; act: "submit" | "approve" | "withdraw" }) =>
      act === "submit" ? tariffsApi.submit(id)
      : act === "approve" ? tariffsApi.approve(id)
      : tariffsApi.withdraw(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tariffs"] }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Rate card deleted" },
    mutationFn: (id: string) => tariffsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tariffs"] }),
  });

  const rowCount = (t: TariffDto) => t.serviceChargeCount + t.priceListLineCount;

  const columns: GridColDef<TariffDto>[] = [
    {
      // W15 — a card and its successor share a code, so the version is what tells two rows with
      // the same code apart. v1 is left unmarked; only a card that has been revised says so.
      field: "code", headerName: "Code", width: 160,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <span>{p.row.code}</span>
          {p.row.version > 1 && (
            <Tooltip title={`Version ${p.row.version} of this card`}>
              <Box component="span"><StatusPill label={`v${p.row.version}`} tone="info" /></Box>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    {
      field: "insuranceSchemeName", headerName: "Payer", width: 170,
      renderCell: (p) => p.row.insuranceSchemeName
        ?? <Box component="span" sx={{ color: "text.disabled" }}>Own card</Box>,
    },
    {
      field: "effectiveFrom", headerName: "Effective", width: 190, sortable: false,
      renderCell: (p) => (
        <span>
          {p.row.effectiveFrom}
          {p.row.effectiveTo ? ` → ${p.row.effectiveTo}` : " → open"}
        </span>
      ),
    },
    { field: "priority", headerName: "Priority", width: 90, type: "number" },
    {
      field: "rows", headerName: "Rows", width: 90, sortable: false, align: "right",
      headerAlign: "right",
      renderCell: (p) => {
        const n = rowCount(p.row);
        // An empty card cannot be activated, so flag it where someone will notice.
        return n === 0
          ? <Tooltip title="No price rows — cannot be activated">
              <Box component="span" sx={{ color: "warning.main" }}>0</Box>
            </Tooltip>
          : <span>{n}</span>;
      },
    },
    {
      // W16 — a status alone does not answer the question this state exists for, which is who
      // signed it off. The name rides under the pill, and the tooltip carries the timestamp.
      field: "status", headerName: "Status", width: 170, sortable: false,
      renderCell: (p) => {
        const who =
          p.row.status === "Submitted" ? p.row.submittedByName
          : p.row.status === "Approved" || p.row.status === "Active" ? p.row.approvedByName
          : null;
        const when =
          p.row.status === "Submitted" ? p.row.submittedAt
          : p.row.status === "Approved" || p.row.status === "Active" ? p.row.approvedAt
          : null;
        const pill = <StatusPill label={p.row.status} tone={STATUS_TONE[p.row.status]} />;

        if (!who) {
          // An Active card with no approver predates W16 — worth saying so plainly rather than
          // leaving a blank that reads as "approved by nobody in particular".
          return p.row.status === "Active" ? (
            <Tooltip title="Activated before approvals were recorded">
              <Box component="span">{pill}</Box>
            </Tooltip>
          ) : pill;
        }

        return (
          <Tooltip title={`${p.row.status === "Submitted" ? "Submitted" : "Approved"} by ${who}${when ? ` · ${stamp(when)}` : ""}`}>
            <Stack spacing={0} sx={{ py: 0.5 }}>
              {pill}
              <Typography variant="caption" color="text.secondary" noWrap>{who}</Typography>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: "actions", headerName: "", width: 160, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          {
            label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage,
            disabledReason: frozen(p.row)
              ? `Frozen while it is ${p.row.status.toLowerCase()} — withdraw it or have it sent back first`
              : undefined,
            onClick: () => { setEditing(p.row); setFormOpen(true); },
          },
          // W16 — the review path, in the order it happens.
          {
            label: "Send for approval",
            icon: <SendIcon fontSize="small" />,
            hidden: !canManage || p.row.status !== "Draft",
            disabledReason: rowCount(p.row) === 0
              ? "Nothing to review — add rates before sending it for approval"
              : undefined,
            onClick: () => reviewMutation.mutate({ id: p.row.id, act: "submit" }),
          },
          {
            label: "Approve — signs off these rates in your name",
            icon: <ThumbUpIcon fontSize="small" />,
            hidden: !canApprove || p.row.status !== "Submitted",
            // The block is on the person, not the permission: whoever put the card up cannot be
            // the one who signs it off, which is the whole of maker/checker.
            disabledReason: p.row.submittedBy === userId
              ? "You submitted this card — somebody else has to approve it"
              : undefined,
            onClick: () => reviewMutation.mutate({ id: p.row.id, act: "approve" }),
          },
          {
            label: "Send back with a reason",
            icon: <ThumbDownIcon fontSize="small" />,
            hidden: !canApprove || p.row.status !== "Submitted",
            disabledReason: p.row.submittedBy === userId
              ? "You submitted this card — withdraw it instead"
              : undefined,
            onClick: () => setRejecting(p.row),
          },
          {
            label: "Withdraw — pull it back out of review",
            icon: <UndoIcon fontSize="small" />,
            hidden: !canManage || p.row.status !== "Submitted" || p.row.submittedBy !== userId,
            onClick: () => reviewMutation.mutate({ id: p.row.id, act: "withdraw" }),
          },
          {
            // W15 — the reason a two-thousand-line payer card is a paste rather than a data-entry
            // project. Only a draft may be loaded, and the preview runs before anything is written.
            label: "Import rates from a spreadsheet",
            icon: <UploadFileIcon fontSize="small" />, hidden: !canManage,
            disabledReason: p.row.status !== "Draft"
              ? `This card is ${p.row.status.toLowerCase()} — create the next version and import into that`
              : undefined,
            onClick: () => setImporting(p.row),
          },
          {
            // W25 — the card stays quoted in its contract currency and bills in it. This is the
            // view that makes it comparable with everything else.
            label: "Report in base currency",
            icon: <CurrencyExchangeIcon fontSize="small" />,
            onClick: () => setReporting(p.row),
          },
          {
            label: "New version — clone every rate into a fresh draft",
            icon: <DifferenceIcon fontSize="small" />, hidden: !canManage,
            disabledReason: rowCount(p.row) === 0
              ? "Nothing to clone — this card has no price rows yet"
              : undefined,
            onClick: () => setVersioning(p.row),
          },
          {
            label: "Activate — its rates start pricing bills",
            icon: <PlayArrowIcon fontSize="small" />,
            // Only an approved card can go live now, so the action appears only where it applies
            // rather than everywhere-but-Active with a reason attached.
            hidden: !canActivate || p.row.status !== "Approved",
            disabledReason: rowCount(p.row) === 0
              ? "Add price rows before activating — an empty card prices nothing"
              : undefined,
            onClick: () => statusMutation.mutate({ id: p.row.id, status: "Active" }),
          },
          {
            label: "Supersede — stops pricing, keeps its history",
            icon: <StopIcon fontSize="small" />,
            hidden: !canActivate || (p.row.status !== "Active" && p.row.status !== "Approved"),
            onClick: () => statusMutation.mutate({ id: p.row.id, status: "Superseded" }),
          },
          {
            label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error",
            hidden: !canManage,
            disabledReason: frozen(p.row)
              ? "Out for approval — withdraw it or have it sent back first"
              : rowCount(p.row) > 0
              ? "Has price rows — supersede it instead so its history survives"
              : undefined,
            onClick: () => {
              if (confirm(`Delete rate card "${p.row.code}"?`)) deleteMutation.mutate(p.row.id);
            },
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Pricing"]}
        title="Rate cards"
        subtitle="A named card that service and item rates can belong to, so a whole payer agreement is activated and reviewed together rather than row by row."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setFormOpen(true); }}>
            New rate card
          </Button>
        ) : undefined}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Draft → Submitted → Approved → Active.</b> Only the last of those prices anything, and a
        card is frozen from the moment it is submitted, so what goes live is exactly what was signed
        off. <b>You cannot approve a card you submitted</b> — somebody else has to. Rates with no
        card attached are unaffected and keep resolving as they always have.
      </Alert>

      {awaitingMe > 0 && canApprove && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => setStatusFilter("Submitted")}>Show them</Button>
          }>
          <b>{awaitingMe} rate card(s) are waiting for approval</b> — somebody has built them and
          they cannot go live until they are reviewed.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by code or name" fullWidth size="small"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select label="Status" size="small" sx={{ minWidth: 160 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TariffStatus | "")}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Submitted">Submitted</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Superseded">Superseded</MenuItem>
          </TextField>
        </Stack>

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {statusMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(statusMutation.error)}</Alert>
        )}
        {reviewMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(reviewMutation.error)}</Alert>
        )}
        {deleteMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>
        )}

        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) =>
              canManage && !frozen(p.row) && (setEditing(p.row), setFormOpen(true))}
          />
        </Box>
      </Paper>

      <TariffDialog open={formOpen} onClose={() => setFormOpen(false)} tariff={editing} />
      <ImportRatesDialog tariff={importing} onClose={() => setImporting(null)} />
      <NextVersionDialog tariff={versioning} onClose={() => setVersioning(null)} />
      <InBaseCurrencyDialog tariff={reporting} onClose={() => setReporting(null)} />
      <RejectDialog tariff={rejecting} onClose={() => setRejecting(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------------------------
// Rejection (W16)
// ---------------------------------------------------------------------------------------------

/**
 * Sends a submitted card back to its maker.
 *
 * <p>The reason is mandatory, and it is the whole substance of the act: a card that comes back with
 * no explanation costs its maker a round of guessing, and the next submission is as likely to be
 * wrong in the same way. It is kept on the draft, so it is still there when they open it.</p>
 */
function RejectDialog({ tariff, onClose }: { tariff: TariffDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");

  useEffect(() => { setReason(""); }, [tariff?.id]);

  const mutation = useMutation({
    meta: { successMessage: "Sent back to its maker" },
    mutationFn: () => tariffsApi.reject(tariff!.id, reason.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tariffs"] });
      onClose();
    },
  });

  if (!tariff) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (reason.trim()) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          Send {tariff.code} back{tariff.version > 1 ? ` (v${tariff.version})` : ""}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            The card returns to <b>Draft</b> and unfreezes, so
            {tariff.submittedByName ? ` ${tariff.submittedByName}` : " its maker"} can change it and
            send it back round. Nothing about it is lost.
          </Alert>

          <TextField
            label="Why is it going back? *" size="small" fullWidth multiline minRows={3} autoFocus
            value={reason} onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
            placeholder="e.g. the consultation rates are the 2025 ones — check against the signed schedule"
            helperText="Kept on the draft, so it is in front of them while they fix it"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="warning"
            disabled={!reason.trim() || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            Send back
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Bulk import (W15)
// ---------------------------------------------------------------------------------------------

interface ParsedRow { rowNumber: number; code: string; rate: number }
interface ParseResult { rows: ParsedRow[]; skipped: { line: number; text: string; why: string }[] }

/**
 * Splits one line into cells, respecting double quotes.
 *
 * <p>The quotes matter more than they look. Excel writes a rate of twelve hundred as
 * <code>"1,200.50"</code>, and a naive split on commas turns that into a cell reading 200.50 — a
 * wrong price that looks entirely plausible in the preview. Any of tab, comma or semicolon
 * separates, because a paste out of Excel is tabs, a saved file is commas, and European exports
 * are semicolons.</p>
 */
function splitCells(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      // "" inside a quoted field is one literal quote.
      if (quoted && line[i + 1] === '"') { cell += '"'; i++; continue; }
      quoted = !quoted;
      continue;
    }
    if (!quoted && (ch === "\t" || ch === "," || ch === ";")) { cells.push(cell); cell = ""; continue; }
    cell += ch;
  }
  cells.push(cell);
  return cells.map(c => c.trim());
}

/**
 * Turns a pasted payer spreadsheet into rows the API understands.
 *
 * <p>Two columns are wanted — a service code and a rate — but the file is somebody else's, so a
 * description column between them is read past rather than refused.</p>
 *
 * <p>Unparseable lines are collected rather than thrown away silently. A header row lands there
 * too, which is the intended outcome: reporting "skipped: Service Code, Rate" tells the operator
 * their file was understood, where dropping it quietly would leave them guessing.</p>
 */
export function parseRateFile(text: string): ParseResult {
  const rows: ParsedRow[] = [];
  const skipped: ParseResult["skipped"] = [];

  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) return;

    const cells = splitCells(line);
    const code = cells[0] ?? "";
    // The rate is the last cell that reads as a number, so a file carrying a description column
    // between the code and the price still lands.
    const rateCell = cells.slice(1).reverse()
      .find(c => c !== "" && Number.isFinite(Number(c.replace(/[₹,\s]/g, ""))));

    if (!code) {
      skipped.push({ line: i + 1, text: line, why: "no code in the first column" });
      return;
    }
    if (rateCell === undefined) {
      skipped.push({ line: i + 1, text: line, why: "no number to read as a rate" });
      return;
    }
    const rate = Number(rateCell.replace(/[₹,\s]/g, ""));
    if (rate < 0) {
      skipped.push({ line: i + 1, text: line, why: "negative rate" });
      return;
    }
    rows.push({ rowNumber: rows.length + 1, code, rate });
  });

  return { rows, skipped };
}

const OUTCOME_TONE = {
  Added: "success", Updated: "info", Unchanged: "neutral", Error: "critical",
} as const;

/**
 * Loads a payer's rate file into a draft card (W15).
 *
 * <p>The dry run is the screen, not a step before it. A payer file is somebody else's data with
 * somebody else's codes in it, and the question worth answering before two thousand prices move is
 * which rows will not match and which rates are about to change. Nothing is written until the
 * preview has been seen and the commit pressed, and the commit is all-or-nothing: one unmatched
 * code leaves the card exactly as it was, because a half-loaded card looks complete and is not.</p>
 */
function ImportRatesDialog({ tariff, onClose }: { tariff: TariffDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [result, setResult] = useState<TariffImportResultDto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => { setText(""); setResult(null); }, [tariff?.id]);

  const parsed = useMemo(() => parseRateFile(text), [text]);
  const payload: TariffImportRowRequest[] =
    parsed.rows.map(r => ({ serviceCode: r.code, rate: r.rate }));

  const run = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) =>
      tariffsApi.import(tariff!.id, payload, dryRun),
    onSuccess: (r) => {
      setResult(r);
      if (r.committed) qc.invalidateQueries({ queryKey: ["tariffs"] });
    },
  });

  // A fresh paste invalidates a preview taken against the old one — showing yesterday's diff over
  // today's file is the one way this screen could actively mislead.
  const onText = (v: string) => { setText(v); setResult(null); };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => onText(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  if (!tariff) return null;

  const committed = result?.committed === true;
  const previewed = !!result && !committed;
  const canCommit = previewed && result.errors === 0 && result.added + result.updated > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <ClosableDialogTitle onClose={onClose}>
        Import rates — {tariff.code}{tariff.version > 1 ? ` v${tariff.version}` : ""}
      </ClosableDialogTitle>
      <DialogContent dividers>
        {run.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(run.error)}</Alert>}

        {committed ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <b>{result.added} added, {result.updated} updated.</b> The card is still a draft — it
            prices nothing until it is activated, so this is the moment to check a few rates before
            switching it on.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Paste two columns — <b>service code</b> and <b>rate</b> — or load a CSV. Excel files
            need saving as CSV first, or copy the two columns straight out of the sheet. Nothing is
            written until you press import, and any unmatched code stops the whole file.
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
            onClick={() => fileInput.current?.click()}>
            Load a CSV
          </Button>
          <input ref={fileInput} type="file" hidden accept=".csv,.tsv,.txt,text/csv,text/plain"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ""; }} />
          {text && (
            <Button size="small" onClick={() => onText("")}>Clear</Button>
          )}
        </Stack>

        <TextField
          multiline minRows={6} maxRows={14} fullWidth size="small"
          placeholder={"CONS-GEN,450\nXRAY-CHEST,300\nECG,250"}
          value={text} onChange={(e) => onText(e.target.value)}
          inputProps={{ style: { fontFamily: "ui-monospace, monospace", fontSize: 13 } }}
        />

        {text.trim() !== "" && (
          <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: "wrap" }}>
            <StatCard label="Rows read" value={parsed.rows.length} />
            <StatCard label="Lines skipped" value={parsed.skipped.length}
              severity={parsed.skipped.length ? "warning" : "default"}
              hint={parsed.skipped.length ? "header rows land here too" : "nothing unreadable"} />
          </Stack>
        )}

        {parsed.skipped.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {parsed.skipped.length} line(s) could not be read and are not part of the import:
            <Box component="ul" sx={{ m: "6px 0 0", pl: 2.5 }}>
              {parsed.skipped.slice(0, 5).map(s => (
                <li key={s.line}>
                  <code>{s.text.slice(0, 60)}</code> — {s.why}
                </li>
              ))}
              {parsed.skipped.length > 5 && <li>…and {parsed.skipped.length - 5} more</li>}
            </Box>
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="overline" color="text.secondary">
              {committed ? "What was imported" : "What importing would do"}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ my: 1.5, flexWrap: "wrap" }}>
              <StatCard label="Added" value={result.added} severity={result.added ? "info" : "default"}
                hint="rates this card did not carry" />
              <StatCard label="Updated" value={result.updated}
                severity={result.updated ? "warning" : "default"} hint="rates that change" />
              <StatCard label="Unchanged" value={result.unchanged} hint="already at this rate" />
              <StatCard label="Problems" value={result.errors}
                severity={result.errors ? "critical" : "default"}
                hint={result.errors ? "these stop the whole import" : "nothing to fix"} />
            </Stack>

            {previewed && result.errors > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {result.errors} row(s) could not be matched, so nothing can be imported — a card
                loaded with the rest would look complete and be missing rates nobody knows about.
                Fix the file and preview again.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>#</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Was</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Problems first: with two thousand rows, the handful that need attention are
                      the only ones worth scrolling to. */}
                  {[...result.rows]
                    .sort((a, b) => Number(b.outcome === "Error") - Number(a.outcome === "Error"))
                    .slice(0, 300)
                    .map(r => (
                      <TableRow key={r.rowNumber} hover>
                        <TableCell>{r.rowNumber}</TableCell>
                        <TableCell>{r.key}</TableCell>
                        <TableCell align="right">{r.rate.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {r.previousRate == null
                            ? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
                            : r.previousRate.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <StatusPill label={r.outcome} tone={OUTCOME_TONE[r.outcome]} />
                            {r.problem && (
                              <Typography variant="caption" color="error.main">{r.problem}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            {result.rows.length > 300 && (
              <Typography variant="caption" color="text.secondary">
                Showing 300 of {result.rows.length} rows. The counts above cover all of them.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{committed ? "Done" : "Cancel"}</Button>
        {!committed && (
          <>
            <Button variant="outlined"
              disabled={parsed.rows.length === 0 || run.isPending}
              startIcon={run.isPending ? <CircularProgress size={16} /> : null}
              onClick={() => run.mutate({ dryRun: true })}>
              Preview {parsed.rows.length > 0 ? `${parsed.rows.length} rows` : ""}
            </Button>
            <Button variant="contained" color="primary"
              disabled={!canCommit || run.isPending}
              onClick={() => run.mutate({ dryRun: false })}>
              Import {previewed ? `${result.added + result.updated} rates` : ""}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Next version (W15)
// ---------------------------------------------------------------------------------------------

/**
 * Clones a card's rows into a fresh draft (W15).
 *
 * <p>An annual increase used to be one revise call per rate, any of which could fail halfway and
 * leave a card half-revised. This is the same change as one draft, checked as a whole and switched
 * on in one act — and activating the clone closes its predecessor the day before, so no date is
 * ever covered by two versions of the same card.</p>
 */
function NextVersionDialog({ tariff, onClose }: { tariff: TariffDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [effectiveTo, setEffectiveTo] = useState("");
  const [adjustmentPct, setAdjustmentPct] = useState("0");

  useEffect(() => {
    if (!tariff) return;
    setEffectiveFrom(today());
    setEffectiveTo(tariff.effectiveTo ?? "");
    setAdjustmentPct("0");
  }, [tariff]);

  const mutation = useMutation({
    meta: { successMessage: "Next version created as a draft" },
    mutationFn: () => tariffsApi.nextVersion(tariff!.id, {
      effectiveFrom,
      effectiveTo: effectiveTo || null,
      adjustmentPct: Number(adjustmentPct || 0),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tariffs"] });
      onClose();
    },
  });

  if (!tariff) return null;

  const pct = Number(adjustmentPct || 0);
  const rows = tariff.serviceChargeCount + tariff.priceListLineCount;
  const err =
    !effectiveFrom ? "The new version needs a start date"
    : effectiveTo && effectiveTo < effectiveFrom ? "A version cannot end before it starts"
    : Number.isNaN(pct) ? "Enter an adjustment, or 0 to clone the rates unchanged"
    : pct <= -100 ? "An adjustment of −100% or steeper would zero the card"
    : pct > 1000 ? "An adjustment above +1000% is almost certainly a typo"
    : null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          New version of {tariff.code} (v{tariff.version} → v{tariff.version + 1})
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            Copies all {rows} rate row(s) into a new <b>draft</b> card with the same code. The
            current version keeps pricing until the new one is activated — that is what makes it
            safe to build and check a revision while the counter is open.
          </Alert>

          <Stack spacing={2}>
            <TextField type="number" label="Across-the-board adjustment (%)" size="small" fullWidth
              value={adjustmentPct} onChange={(e) => setAdjustmentPct(e.target.value)}
              inputProps={{ step: "0.01", min: -99.99, max: 1000 }}
              helperText={pct === 0
                ? "0 clones every rate unchanged — use this to load a payer's new file over it"
                : `Every absolute rate moves by ${pct > 0 ? "+" : ""}${pct}%. Derived rates are left alone — they already follow their base.`} />

            <Stack direction="row" spacing={2}>
              <TextField type="date" label="Effective from *" size="small" fullWidth
                value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={`v${tariff.version} closes the day before this`} />
              <TextField type="date" label="Effective to" size="small" fullWidth
                value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                InputLabelProps={{ shrink: true }} helperText="Blank = open-ended" />
            </Stack>

            {err && <Alert severity="info">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            Create draft
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function TariffDialog({
  open, onClose, tariff,
}: { open: boolean; onClose: () => void; tariff: TariffDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!tariff;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [insuranceSchemeId, setInsuranceSchemeId] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [effectiveTo, setEffectiveTo] = useState("");
  const [priority, setPriority] = useState(100);

  useEffect(() => {
    if (!open) return;
    setCode(tariff?.code ?? "");
    setName(tariff?.name ?? "");
    setDescription(tariff?.description ?? "");
    setInsuranceSchemeId(tariff?.insuranceSchemeId ?? "");
    setCurrencyId(tariff?.currencyId ?? "");
    setEffectiveFrom(tariff?.effectiveFrom ?? today());
    setEffectiveTo(tariff?.effectiveTo ?? "");
    setPriority(tariff?.priority ?? 100);
  }, [open, tariff]);

  const schemes = useQuery({
    queryKey: ["insurance-schemes", "options"],
    queryFn: () => insuranceSchemesApi.list({ isActive: true, pageSize: 200 }),
    staleTime: 5 * 60_000, enabled: open,
  });
  const currencies = useQuery({
    queryKey: ["currencies", "options"],
    queryFn: () => currenciesApi.list({ isActive: true, pageSize: 100 }),
    staleTime: 5 * 60_000, enabled: open,
  });

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Rate card updated" : "Rate card created" },
    mutationFn: async () => {
      const payload: UpsertTariffRequest = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        insuranceSchemeId: insuranceSchemeId || null,
        currencyId: currencyId || null,
        effectiveFrom,
        effectiveTo: effectiveTo || null,
        priority,
      };
      if (isEdit) await tariffsApi.update(tariff!.id, payload);
      else await tariffsApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tariffs"] });
      onClose();
    },
  });

  // Frozen is not listed here: the alert above already says it at length, and repeating it in the
  // small print under the form would be the same sentence twice.
  const locked = isEdit && frozen(tariff!);
  const err =
    !code.trim() ? "Code required"
    : !name.trim() ? "Name required"
    : effectiveTo && effectiveTo < effectiveFrom ? "A card cannot end before it starts"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err && !locked) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? `Edit ${tariff!.code}` : "New rate card"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
          )}
          {!isEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Created as a draft. Attach rates to it, then activate it separately.
            </Alert>
          )}
          {isEdit && tariff!.status === "Active" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This card is live. Changing its window or priority changes what patients are
              charged from the next bill onwards.
            </Alert>
          )}
          {/* W16 — the reason it came back, in front of the person fixing it. */}
          {isEdit && tariff!.status === "Draft" && tariff!.rejectionReason && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <b>Sent back:</b> {tariff!.rejectionReason}
            </Alert>
          )}
          {isEdit && frozen(tariff!) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This card is {tariff!.status.toLowerCase()} and frozen — nothing on it can change
              while it is being reviewed, or what goes live would not be what was signed off.
              Withdraw it, or have it sent back, first.
            </Alert>
          )}

          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField label="Code *" size="small" fullWidth autoFocus
                value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={isEdit}
                helperText={isEdit ? "Code cannot be changed" : "e.g. CGHS-2026"} />
              <TextField type="number" label="Priority" size="small" sx={{ width: 160 }}
                value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)}
                helperText="Higher wins ties"
                inputProps={{ min: 0, max: 10000 }} />
            </Stack>

            <TextField label="Name *" size="small" fullWidth
              value={name} onChange={(e) => setName(e.target.value)} />

            <TextField label="Description" size="small" fullWidth multiline minRows={2}
              value={description} onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 1000 }} />

            <Stack direction="row" spacing={2}>
              <TextField select label="Payer scheme" size="small" fullWidth
                value={insuranceSchemeId}
                onChange={(e) => setInsuranceSchemeId(e.target.value)}
                helperText="Blank = the tenant's own published card">
                <MenuItem value="">— Own card —</MenuItem>
                {(schemes.data?.items ?? []).map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.code} — {s.name}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Currency" size="small" sx={{ minWidth: 180 }}
                value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}
                helperText="Blank = base currency">
                <MenuItem value="">— Base —</MenuItem>
                {(currencies.data?.items ?? []).map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.code}</MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField type="date" label="Effective from *" size="small" fullWidth
                value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="Effective to" size="small" fullWidth
                value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Blank = open-ended" />
            </Stack>

            {err && <Alert severity="info">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || locked || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Reported in base currency (W25)
// ---------------------------------------------------------------------------------------------

/**
 * A rate card's rows restated in the tenant's reporting currency.
 *
 * The card itself is unchanged — it is quoted in whatever the contract says, and it bills in that.
 * This is the view that makes it comparable with everything else, which is the one thing a
 * foreign-currency card could never do before there were rates on file.
 *
 * Rows that could not be converted are listed with the reason and counted out of the total, and
 * the total says so. A figure that quietly added dollars to rupees would be worse than no figure.
 */
function InBaseCurrencyDialog({ tariff, onClose }: { tariff: TariffDto | null; onClose: () => void }) {
  const [on, setOn] = useState(todayIso);

  useEffect(() => {
    if (tariff) setOn(todayIso());
  }, [tariff]);

  const query = useQuery({
    queryKey: ["tariff-in-base", tariff?.id, on],
    queryFn: () => tariffsApi.inBaseCurrency(tariff!.id, on),
    enabled: !!tariff,
  });

  const data = query.data;
  const money = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const columns: GridColDef<ConvertedChargeRow>[] = [
    { field: "serviceItemName", headerName: "Item", flex: 1, minWidth: 220 },
    {
      field: "rate", headerName: "As quoted", width: 150, align: "right", headerAlign: "right",
      renderCell: (p) => <span>{money(p.row.rate)} {p.row.currencyCode ?? ""}</span>,
    },
    {
      field: "exchangeRateUsed", headerName: "At", width: 120, align: "right", headerAlign: "right",
      renderCell: (p) => p.row.exchangeRateUsed
        ? <span>&times;{p.row.exchangeRateUsed.toLocaleString(undefined, {
            minimumFractionDigits: 2, maximumFractionDigits: 6 })}</span>
        : <Box component="span" sx={{ color: "text.disabled" }}>—</Box>,
    },
    {
      field: "rateInBase", headerName: `In ${data?.baseCurrencyCode ?? "base"}`,
      width: 180, align: "right", headerAlign: "right",
      renderCell: (p) => p.row.rateInBase != null
        ? <b>{money(p.row.rateInBase)}</b>
        // The whole point of the feature is that this cell says why rather than showing a number
        // that would be added into a total it does not belong in.
        : <Box component="span" sx={{ color: "warning.main" }}>not converted</Box>,
    },
    {
      field: "problem", headerName: "Why not", flex: 1, minWidth: 220,
      valueGetter: (_v, row) => row.problem || "",
    },
  ];

  return (
    <Dialog open={!!tariff} onClose={onClose} maxWidth="lg" fullWidth>
      <ClosableDialogTitle onClose={onClose}>
        {tariff ? `${tariff.code} — reported in base currency` : ""}
      </ClosableDialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="Rates in force on" size="small" type="date"
            value={on} onChange={(e) => setOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Uses the rate effective on this date — an old report re-runs the same way"
          />
          {data && (
            <>
              <StatCard label="Rows" value={String(data.rowCount)} />
              <StatCard
                label={`Total in ${data.baseCurrencyCode ?? "base"}`}
                value={money(data.totalInBase)}
              />
              {data.unconvertedCount > 0 && (
                <StatCard label="Not converted" value={String(data.unconvertedCount)} severity="warning" />
              )}
            </>
          )}
        </Box>

        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

        {data && !data.baseCurrencyCode && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No base currency is set for this tenant, so there is nothing to report in. Mark one
            currency as the base in Catalog &rarr; Currencies.
          </Alert>
        )}
        {data && data.unconvertedCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {data.unconvertedCount} of {data.rowCount} rows could not be converted and are{" "}
            <b>left out of the total above</b>. Add the missing rate in Catalog &rarr; Currencies
            and re-run — the figure is not complete until this reads zero.
          </Alert>
        )}
        {data && data.unconvertedCount === 0 && data.rowCount > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Every row converted. The total is the whole card, restated in{" "}
            {data.baseCurrencyCode}.
          </Alert>
        )}

        <Box sx={{ height: 460 }}>
          <DataGrid
            rows={data?.rows ?? []}
            columns={columns}
            loading={query.isLoading}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
            getRowId={(row) => row.serviceChargeId}
            getRowClassName={(p) => p.row.rateInBase == null ? "row-unconverted" : ""}
            // A neutral tint, not a warning-coloured one: the reason is already spelled out in the row.
            sx={{ "& .row-unconverted": { bgcolor: "action.hover" } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
